/**
 * CopyTextButton component for copying text to clipboard.
 */

import React, { useState } from 'react';

/**
 * Props for the CopyTextButton component.
 */
interface CopyTextButtonProps {
  /** The text to copy to clipboard */
  textToCopy: string;

  /** Test ID for the button */
  testId: string;

  /** Optional className for additional styling */
  className?: string;

  /** Callback function called on successful copy */
  onCopySuccess?: () => void;

  /** Callback function called on copy error */
  onCopyError?: (error: Error) => void;
}

/**
 * CopyTextButton component for copying text to clipboard.
 * @param props - Props for the CopyTextButton component.
 * @returns JSX.Element representing the CopyTextButton component.
 */
const CopyTextButton: React.FC<CopyTextButtonProps> = ({
  textToCopy,
  testId,
  className = '',
  onCopySuccess,
  onCopyError,
}: CopyTextButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);

      // Call success callback if provided
      if (onCopySuccess) {
        onCopySuccess();
      }

      setCopied(false);
    } catch (error) {
      // Call error callback if provided
      if (onCopyError) {
        onCopyError(error as Error);
      }
    }
  };

  return (
    <button
      onClick={handleCopy}
      data-testid={testId}
      className={`request-item_button request-item_copy ${className}`.trim()}
    >
      <span className="bl-icon bl-icon-copy request-item_button-icon"></span>
    </button>
  );
};

export default CopyTextButton;
